import { Filter } from "@faire/retailer-visitor-shared/components/Filters/Filter";
import { FilterName } from "@faire/retailer-visitor-shared/components/Filters/FilterName";
import { useCategoryPageFilterKeys } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageFilterKeys";
import { useCategoryPageFormattedTotalResults } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useCategoryPageFormattedTotalResults";
import { useIsCategoryPageFiltersLoading } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageFiltersLoading";
import { useIsCategoryPageFilterSplitEnabled } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageFilterSplitEnabled";
import {
  FILTERS_TIMING_FETCH_END_KEY,
  FILTERS_TIMING_FETCH_START_KEY,
  PageLoadTimingSectionName,
} from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/consts";
import { useCurrentNavigationPath } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useCurrentNavigationPath";
import { useOneIndexedPageNumber } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useOneIndexedPageNumber";
import { ITraceEventMarker } from "@faire/web--source/common/pete/TraceEventTracker";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import {
  trackCategoriesLoadPageSectionTiming,
  trackCategoriesLoadPageSectionTimingTraceApiRequestEnd,
  trackCategoriesLoadPageSectionTimingTraceApiRequestStart,
  trackCategoriesLoadPageSectionTimingTraceRendered,
} from "@faire/web-api--source/events/categories/timing/loadPageSection";
import isEqual from "lodash/isEqual";
import React, { useLayoutEffect, useMemo, useRef } from "react";
import styled from "styled-components";

const useFiltersLoadTiming = () => {
  const isFilterSplitEnabled = useIsCategoryPageFilterSplitEnabled();
  const isFiltersLoading = useIsCategoryPageFiltersLoading();
  const filtersTimingRef = useRef<ITraceEventMarker | undefined>(undefined);
  const navigationPath = useCurrentNavigationPath();
  const filterKeys = useCategoryPageFilterKeys();
  const pageNumber = useOneIndexedPageNumber();

  const previousFiltersRef = useRef(filterKeys);
  const pageNumberRef = useRef(pageNumber);

  const pageNumberChanged = useMemo(() => {
    if (!isEqual(previousFiltersRef.current, filterKeys)) {
      // filter keys changed -- page number is automatically reset to 1
      previousFiltersRef.current = filterKeys;
      pageNumberRef.current = 1;
      return false;
    } else if (pageNumberRef.current !== pageNumber) {
      // page number changed
      pageNumberRef.current = pageNumber;
      return true;
    }

    return false;
  }, [filterKeys, pageNumber]);

  const trackMounted = useEffectEvent(() => {
    if (pageNumberChanged && isFilterSplitEnabled) {
      // filters are not re-mounted on page number change
      return;
    }
    filtersTimingRef.current = trackCategoriesLoadPageSectionTiming(
      navigationPath,
      PageLoadTimingSectionName.FILTERS
    );
  });

  useLayoutEffect(() => {
    trackMounted();
  }, [trackMounted, filterKeys, pageNumberChanged, isFilterSplitEnabled]);

  useLayoutEffect(() => {
    if (!filtersTimingRef.current) {
      return;
    }

    if (!isFiltersLoading) {
      const startFetchTime =
        performance
          .getEntriesByName?.(FILTERS_TIMING_FETCH_START_KEY)
          .slice(-1)?.[0]?.startTime ?? 0;
      const endFetchTime =
        performance
          .getEntriesByName?.(FILTERS_TIMING_FETCH_END_KEY)
          .slice(-1)?.[0]?.startTime ?? 0;

      // don't emit API request start/end traces for cached queries
      if (
        isFilterSplitEnabled &&
        startFetchTime > filtersTimingRef.current.sourceEventStart
      ) {
        trackCategoriesLoadPageSectionTimingTraceApiRequestStart(
          filtersTimingRef.current,
          { timestampOverride: startFetchTime }
        );
        trackCategoriesLoadPageSectionTimingTraceApiRequestEnd(
          filtersTimingRef.current,
          { timestampOverride: endFetchTime }
        );
      }

      trackCategoriesLoadPageSectionTimingTraceRendered(
        filtersTimingRef.current
      );
    }
  }, [isFiltersLoading, isFilterSplitEnabled]);
};

export const CategoryPageFilters = () => {
  const formattedTotalResults = useCategoryPageFormattedTotalResults();
  const isFiltersLoading = useIsCategoryPageFiltersLoading();
  useFiltersLoadTiming();

  return (
    <StyledFilter
      name={FilterName.LOGGED_IN_CATEGORY}
      resultsCount={formattedTotalResults ?? ""}
      isLoading={isFiltersLoading}
      headerSeparator
      isSticky
    />
  );
};

const StyledFilter = styled(Filter)`
  margin-top: ${getSpacing("1x")};
`;
